import { type ApolloError, gql, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useTenant } from "@/providers/session";

import { GET_ALL_TRANSACTIONS } from "./get-all";

type DeleteBankTransactionInput = {
  bankTransactionIds: string[];
};

export const useDeleteBankTransaction = (): [
  (input: DeleteBankTransactionInput) => Promise<number>,
  {
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [deleteBankTransactions, { loading, error }] = useMutation<{
    deleteBankTransactions: number;
  }>(DELETE_BANK_TRANSACTIONS_MUTATION, {
    refetchQueries: [GET_ALL_TRANSACTIONS],
    update: (cache, { data }) => {
      if (!data?.deleteBankTransactions) return;

      cache.evict({ fieldName: "transactions" });
      cache.gc();
    },
  });

  const handleDeleteBankTransaction = async (
    input: DeleteBankTransactionInput,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    if (!input.bankTransactionIds || input.bankTransactionIds.length === 0) {
      throw new Error("Bank transaction IDs are required");
    }

    const toastId = toast.loading(t("generic_loading"));

    try {
      const { data: response } = await deleteBankTransactions({
        variables: {
          bankTransactionIds: input.bankTransactionIds,
        },
      });

      if (!response) {
        throw new Error("No response from deleteBankTransactions");
      }

      toast.dismiss(toastId);
      const translationKey = "transactions.transactions_deleted_successfully";

      toast.success(
        t(translationKey, {
          count: response.deleteBankTransactions,
        }) ||
          `${response.deleteBankTransactions} transaction(s) deleted successfully`,
        { richColors: true },
      );

      return response.deleteBankTransactions;
    } catch (error) {
      console.log("ERROR", error);
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleDeleteBankTransaction, { loading, error }];
};

export const DELETE_BANK_TRANSACTIONS_MUTATION = gql`
  mutation DeleteBankTransactions($bankTransactionIds: [String!]!) {
    deleteBankTransactions(bankTransactionIds: $bankTransactionIds)
  }
`;
